import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  SummaryDisplayItem,
  UseDataProps,
} from '50.quickConnect.Fields/FieldsTypes/Containers/DialogQcField/DialogSummary/types';
import { DialogSummaryName } from '50.quickConnect.Fields/FieldsTypes/Containers/DialogQcField/DialogSummary/const';
import { FieldDesc } from '90.quickConnect.Models/models';
import { shouldExcludeFromSummaryDisplay, isEmptyFieldValue } from '90.quickConnect.Models/guards';
import getSummaryLabel from '50.quickConnect.Fields/helpers/summary/getSummaryLabel';

const getItemsToDisplay = (items: FieldDesc[], t: TFunction): SummaryDisplayItem[] => {
  const result: SummaryDisplayItem[] = [];
  const stack: FieldDesc[] = [...items].reverse();

  while (stack.length > 0) {
    const field = stack.pop();

    if (!field) continue;

    /*
     * Un champ invisible ne doit pas être affiché et ses enfants
     * ne doivent normalement pas être parcourus non plus.
     */
    if (field.visible === false || !field.isVisible) continue;

    /*
     * Exclure les conteneurs qui ne doivent pas apparaître dans le résumé.
     * Les Include sont transparents - leurs enfants seront affichés directement.
     *
     * Si les enfants doivent malgré tout apparaître, il faudra déplacer
     * ce contrôle après l'ajout des enfants dans la pile.
     */
    if (shouldExcludeFromSummaryDisplay(field)) continue;

    const children = field.items;

    if (children?.length) {
      for (let index = children.length - 1; index >= 0; index -= 1) {
        stack.push(children[index]);
      }
    }

    if (isEmptyFieldValue(field.value)) continue;

    const summaryLabel = getSummaryLabel(field, t);

    if (!summaryLabel || summaryLabel.trim().length === 0) continue;

    result.push({
      ...field,
      summaryLabel,
    });
  }

  return result;
};

const useData = (items: FieldDesc[], parentId: string): UseDataProps => {
  const { t } = useTranslation('declaration');

  const itemsToDisplay = getItemsToDisplay(items, t);

  return {
    nameWithId: `${DialogSummaryName}-${parentId}`,
    hasItemsToDisplay: itemsToDisplay.length > 0,
    itemsToDisplay,
  };
};

export default useData;
